import { FC, useCallback, useMemo } from 'react';
import { graphql, useRefetchableFragment } from 'react-relay';
import { Heart } from 'dibs-portfolio-heart/exports/Heart';
import { Maybe } from 'dibs-ts-utils/exports/Maybe';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

import { RecommendedItemHeart_viewer$key } from './__generated__/RecommendedItemHeart_viewer.graphql';

const viewerFragment = graphql`
    fragment RecommendedItemHeart_viewer on Viewer
    @refetchable(queryName: "RecommendedItemHeartViewerRefetchQuery")
    @argumentDefinitions(
        loadPortfolioData: { type: "Boolean", defaultValue: false }
        selectedItemIds: { type: "[String]", defaultValue: [] }
        userIds: { type: "[String]", defaultValue: [] }
    ) {
        portfolioItemMatches(itemIds: $selectedItemIds, userIds: $userIds, portfolioTypes: [HEART])
            @include(if: $loadPortfolioData) {
            ...Heart_heartPortfolioItems
            item {
                serviceId
            }
        }
        ...Heart_viewer @arguments(userIds: $userIds, loadPortfolioData: $loadPortfolioData)
    }
`;

type FavoritesRefetch = (callback: (error: Error | null | undefined) => void) => void;

type Props = {
    itemId: string | null;
    className?: string;
    buttonClass?: string;
    viewer: RecommendedItemHeart_viewer$key;
    userId?: string | null;
    itemsIds: Maybe<string>[];
    onFavorited?: ({ itemId }: { itemId: string | null }) => void;
    onUnFavorited?: ({ itemId }: { itemId: string | null }) => void;
};

export const RecommendedItemHeart: FC<Props> = ({
    itemId,
    className,
    buttonClass,
    viewer: viewerRef,
    userId,
    itemsIds,
    onFavorited = () => {},
    onUnFavorited = () => {},
}) => {
    const [viewer, refetch] = useRefetchableFragment(viewerFragment, viewerRef);

    const doRefetch: FavoritesRefetch = useCallback(
        callback => {
            refetch(
                {
                    loadPortfolioData: true,
                    selectedItemIds: itemsIds,
                    userIds: userId ? [userId] : [],
                },
                {
                    onComplete: callback,
                    //Will prevent suspense on the whole carousel
                    fetchPolicy: 'store-and-network',
                }
            );
        },
        [itemsIds, refetch, userId]
    );

    const authModalShow = useCallback((favoriteItem: $TSFixMe) => {
        authModalLoader
            .show({
                flow: authModalLoader.constants.SAVE_ITEM_FLOW,
                ga: {
                    label: 'save item - similar items page',
                },
            })
            .then(favoriteItem);
    }, []);

    const itemMatch = useMemo(() => {
        const portfolioItemMatches = viewer.portfolioItemMatches || [];
        return portfolioItemMatches.filter(heart => heart?.item?.serviceId === itemId);
    }, [viewer.portfolioItemMatches, itemId]);

    return (
        <Heart
            theme="dark"
            itemId={itemId}
            viewer={viewer}
            userId={userId}
            heartPortfolioItems={itemMatch}
            doRefetch={doRefetch}
            authModalShow={authModalShow}
            className={className}
            buttonClass={buttonClass}
            onFavorited={() => onFavorited({ itemId })}
            onUnFavorited={() => onUnFavorited({ itemId })}
        />
    );
};
